import { useEffect, useState } from "react";
import { Store, Copy, Share2, Check, Loader2, Lock } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { track } from "@/lib/analytics";

const ORIGIN = "https://www.agroaluga.com.br";

function toSlug(s: string): string {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function MinhaLojaCard({ plan }: { plan: any }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [slug, setSlug] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [nome, setNome] = useState("");
  const [checando, setChecando] = useState(false);
  const [disponivel, setDisponivel] = useState<boolean | null>(null);
  const [salvandoSlug, setSalvandoSlug] = useState(false);

  const elegivel = !!plan && (plan.is_admin || plan.plan_id === "pro" || plan.plan_id === "ultra");
  const slugPreview = toSlug(nome);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }
      const { data } = await supabase.from("profiles").select("store_enabled, store_slug").eq("id", user.id).maybeSingle();
      if (!active) return;
      setEnabled(!!data?.store_enabled);
      setSlug(data?.store_slug ?? null);
      setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (slug || !enabled) return;
    if (slugPreview.length < 3) { setDisponivel(null); return; }
    setChecando(true);
    const t = setTimeout(async () => {
      const { data } = await supabase.rpc("store_slug_disponivel", { p_slug: slugPreview });
      setDisponivel(data === true);
      setChecando(false);
    }, 450);
    return () => clearTimeout(t);
  }, [slugPreview, slug, enabled]);

  const storeUrl = slug ? ORIGIN + "/loja/" + slug : "";

  async function toggle() {
    if (saving) return;
    setSaving(true);
    const novo = !enabled;
    const { data, error } = await supabase.rpc("set_my_store", { p_enabled: novo });
    setSaving(false);
    if (error || !(data as any)?.ok) {
      const motivo = (data as any)?.error;
      toast({ description: motivo === "plan_not_allowed" ? "A Minha Loja está disponível apenas para os planos Pro e Ultra." : "Não foi possível atualizar a loja. Tente novamente.", variant: "destructive" });
      return;
    }
    setEnabled(novo);
    track(novo ? "loja_ativada" : "loja_desativada", { type: "click" });
    toast({ description: novo ? "Minha Loja ativada!" : "Minha Loja desativada." });
  }

  async function salvarSlug() {
    if (salvandoSlug) return;
    if (slugPreview.length < 3) { toast({ description: "O nome deve ter ao menos 3 caracteres.", variant: "destructive" }); return; }
    setSalvandoSlug(true);
    const { data, error } = await supabase.rpc("set_store_slug", { p_slug: nome });
    setSalvandoSlug(false);
    const res = data as any;
    if (error || !res?.ok) {
      const map = { taken: "Esse nome já está em uso. Escolha outro.", invalid_length: "O nome deve ter entre 3 e 30 caracteres.", plan_not_allowed: "Disponível apenas para planos Pro e Ultra.", already_set: "Você já definiu o nome da sua loja." };
      toast({ description: map[res?.error] || "Não foi possível definir o nome. Tente novamente.", variant: "destructive" });
      if (res?.error === "already_set" && res?.slug) setSlug(res.slug);
      return;
    }
    setSlug(res.slug);
    track("loja_slug_definido", { type: "click" });
    toast({ description: "Nome da loja definido com sucesso!" });
  }

  async function copiar() {
    try { await navigator.clipboard.writeText(storeUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { toast({ description: "Não foi possível copiar.", variant: "destructive" }); }
  }

  async function compartilhar() {
    track("loja_compartilhada", { type: "click" });
    if (navigator.share) { try { await navigator.share({ title: "AgroAluga", text: "Confira minha loja no AgroAluga", url: storeUrl }); } catch {} }
    else { copiar(); }
  }

  if (!elegivel) return null;
  if (loading) return (<div className="mb-6 bg-card border border-border rounded-xl p-5 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>);

  return (
    <div className="mb-6 bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-sm font-heading font-bold flex items-center gap-2"><Store className="h-4 w-4 text-primary" /> Minha Loja</h2>
          <p className="text-xs text-muted-foreground mt-1">Transforme seu perfil numa loja pública com todos os seus anúncios e sua reputação.</p>
        </div>
        <button onClick={toggle} disabled={saving} role="switch" aria-checked={enabled} className={"relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors " + (enabled ? "bg-primary" : "bg-secondary") + (saving ? " opacity-60" : "")}>
          <span className={"inline-block h-5 w-5 transform rounded-full bg-white transition-transform " + (enabled ? "translate-x-5" : "translate-x-0.5")} />
        </button>
      </div>

      {enabled && !slug && (
        <div className="mt-3 space-y-3 border-t border-border pt-4">
          <div>
            <label className="text-sm font-medium block mb-1">Nome da sua loja</label>
            <p className="text-xs text-muted-foreground mb-2">Escolha com cuidado: o nome do link <strong>não poderá ser alterado</strong> depois (apenas via suporte).</p>
            <input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={40} placeholder="Ex: Tratores Blumenau" className="w-full bg-muted rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
          {slugPreview.length >= 3 && (
            <div className="text-xs flex items-center gap-2 flex-wrap">
              <span className="text-muted-foreground">Seu link:</span>
              <span className="font-mono text-foreground">agroaluga.com.br/loja/{slugPreview}</span>
              {checando ? <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" /> : disponivel === true ? <span className="text-green-600 font-medium flex items-center gap-1"><Check className="h-3 w-3" /> disponível</span> : disponivel === false ? <span className="text-destructive font-medium">já em uso</span> : null}
            </div>
          )}
          <button onClick={salvarSlug} disabled={salvandoSlug || slugPreview.length < 3 || disponivel === false} className="w-full text-sm font-semibold bg-primary text-primary-foreground rounded-md py-2.5 hover:bg-primary/90 transition-colors disabled:opacity-50">
            {salvandoSlug ? "Salvando..." : "Definir nome da loja"}
          </button>
        </div>
      )}

      {enabled && slug && (
        <div className="mt-3 space-y-3 border-t border-border pt-4">
          <div className="flex items-center gap-2 bg-muted rounded-md px-3 py-2">
            <span className="text-sm text-foreground truncate flex-1">{storeUrl.replace("https://www.", "")}</span>
            <button onClick={copiar} className="text-primary hover:text-primary/80 shrink-0" aria-label="Copiar link">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> Para alterar o nome da loja, entre em contato com o suporte.</p>
          <div className="flex gap-2">
            <a href={storeUrl} target="_blank" rel="noreferrer" className="flex-1 text-center text-sm font-semibold border border-border rounded-md py-2 hover:bg-muted transition-colors">Ver minha loja</a>
            <button onClick={compartilhar} className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold bg-primary text-primary-foreground rounded-md py-2 hover:bg-primary/90 transition-colors"><Share2 className="h-4 w-4" /> Compartilhar</button>
          </div>
        </div>
      )}
    </div>
  );
}
